/**
 * Hooks del módulo Admin.
 *
 * Viven acá y no en `lib/queries.ts` porque `lib/` es de los cimientos y lo
 * comparten los seis módulos: agregarle las tres rutas de Admin es tocar un
 * archivo que otros cinco están leyendo al mismo tiempo. Lo que **no** cambia es
 * la regla: se sale por la única puerta HTTP (`lib/api`), nunca con `fetch`, y
 * las claves se invalidan con el mismo namespace que usa `lib/queries.ts` para
 * que una edición del catálogo también refresque `/api/metrics` y el Overview.
 */
import type {
  AdminPayload,
  MetricDefinition,
  UpdateUserRoleInput,
  UpsertMetricDefinitionInput,
  User,
} from '@ops/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiGet, apiPut } from '../../lib/api'
import { queryKeys } from '../../lib/queries'

export const adminKeys = {
  admin: ['admin'] as const,
}

/**
 * Los tres bloques de la pantalla en un request. `retry: false` a propósito: el
 * 403 de un rol sin permiso es una respuesta definitiva, no un error de red, y
 * reintentarlo tres veces solo demora la pantalla de "sin acceso".
 */
export function useAdmin() {
  return useQuery({
    queryKey: adminKeys.admin,
    queryFn: () => apiGet<AdminPayload>('/api/admin'),
    retry: false,
  })
}

/** Cambia el rol de una cuenta. La autoridad es el servidor: acá solo se pide. */
export function useUpdateUserRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateUserRoleInput) =>
      apiPut<User>(
        `/api/admin/users/${encodeURIComponent(input.userId)}`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.admin })
      void qc.invalidateQueries({ queryKey: queryKeys.users })
      // Si el rol cambiado es el del usuario del request, la navegación entera
      // cambia: hay que volver a preguntar quién es.
      void qc.invalidateQueries({ queryKey: queryKeys.me })
    },
  })
}

/**
 * Alta y edición de un KPI del catálogo — la decisión de arquitectura del spec:
 * agregar una métrica es este PUT, no un deploy. Invalida `metrics` y `overview`
 * porque cambiar un umbral cambia el semáforo de todas las pantallas.
 */
export function useUpsertMetricDefinition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpsertMetricDefinitionInput) =>
      apiPut<MetricDefinition>(
        `/api/admin/metrics/${encodeURIComponent(input.slug)}`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.admin })
      void qc.invalidateQueries({ queryKey: ['metrics'] })
      void qc.invalidateQueries({ queryKey: ['overview'] })
    },
  })
}
